import EditLayout from 'layouts/edit-layout/EditLayout.tsx'
import { useTranslation } from 'react-i18next'
import { Banner, Button, Card, Heading, StepList } from '@boltenergy-be/design-system'
import styles from './MysterySwitch.module.scss'
import { useStoreSelector } from 'hooks/store.ts'
import { routes } from 'types/routes.ts'
import { useState } from 'react'
import { selectCurrentBillingContract } from 'store/contact/slice.ts'
import { formatAddress } from 'utils/user.ts'
import boltBackend from 'lib/bolt-backend'
import { log } from 'utils/logging.ts'

const MysterySwitch = () => {
  // Redux store
  const { contact } = useStoreSelector((store) => store.contact)
  const billingContract = useStoreSelector(selectCurrentBillingContract)

  // i18n
  const { t } = useTranslation(['mysterySwitch', 'common', 'errors'])

  // Local state
  const [view, setView] = useState<'overview' | 'confirmation'>('overview')
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)

  const handleConfirm = async () => {
    setLoading(true)

    try {
      const { success } = await boltBackend.postMysterySwitchCase({
        customerNumber: billingContract.customerNumber,
        eanElectricity: billingContract.serviceContracts.electricity.deliveryPoint.ean,
        eanGas: billingContract.serviceContracts.gas?.deliveryPoint?.ean
      })

      if (success) {
        setView('confirmation')
      } else {
        setError(true)
      }
    } catch (err) {
      setError(true)
      log({
        error: err instanceof Error ? err : 'Could not create mystery switch case',
        identifier: '[cases:mystery-switch]'
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <EditLayout
      withFooter
      title={
        <Heading as="h1" variant="h5">
          {t(($) => $.title)}
        </Heading>
      }
      backUrl={routes.OVERVIEW}
    >
      <Card className={styles.card}>
        {view === 'overview' && (
          <>
            <small>{formatAddress(billingContract.serviceContracts.electricity.deliveryPoint.address)}</small>
            <div className="flex-col gap-200">
              <Heading as="h2" variant="h4">
                {t(($) => $.overview.title)}
              </Heading>
              <p className="white-space-pre-line">{t(($) => $.overview.description)}</p>
            </div>

            {error ? (
              <Banner
                type="blocking"
                title={t(($) => $.default, {
                  ns: 'errors'
                })}
              />
            ) : (
              <Button type="button" onClick={handleConfirm} {...{ loading }}>
                {t(($) => $.confirm, {
                  ns: 'common'
                })}
              </Button>
            )}
          </>
        )}

        {view === 'confirmation' && (
          <>
            <Heading as="h2" variant="h4">
              {t(($) => $.confirmation.title, {
                name: contact?.firstName || ''
              })}
            </Heading>

            <Heading as="h3" variant="h6">
              {t(($) => $.confirmation.subtitle)}
            </Heading>

            <StepList size="small" className={styles.steps}>
              {t(($) => $.confirmation.steps, {
                returnObjects: true
              }).map((step, index) => (
                <li key={index}>
                  <div className="flex-col">
                    <strong>{step.title}</strong>
                    <p>{step.description}</p>
                  </div>
                </li>
              ))}
            </StepList>
          </>
        )}
      </Card>
    </EditLayout>
  )
}

export default MysterySwitch
